import { Formik } from "formik";
import * as Yup from "yup";
import CheveronUpIcon from "assets/icons/HeroIcons/CheveronUpIcon";
import { CheveronDownIcon } from "assets/icons/HeroIcons";
import CustomCheckbox from "view/components/CheckBox";
import splitString from "utils/splitString";
// Components
import Button from "view/components/Button";
import TextInput from "view/pages/MyProfile/Components/Inputs/TextInput";
import { Toasts } from "view/components/Toasts";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import apiLibrary from "services/api";
import {
  openAllLayersListingScreen,
  removeAllDrawShapes,
  resetMap,
  updateSelectedLayersItems,
  updateSelectedShape,
} from "store/geography";
import { useDispatch } from "react-redux";
import RectangleIcon from "assets/icons/MapIcons/RectangleIcon";
import CircleIcon from "assets/icons/MapIcons/CircleIcon";
import PolygonIcon from "assets/icons/MapIcons/PolygonIcon";
import { shapeTypes } from "store/geography/initialState";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { showSnapingControl } from "store/geography/reducer.actions";
import ColorPicker from "Components/Geography/ColorPicker";
import willColorBeVisibleOnMap from "utils/willColorBeVisibleOnMap";
import { useFetchGlobalAndCommunityVectorLayers } from "Components/Geography/SideBar/hooks";
import { EntityType } from "Components/Geography/SideBar/hooks/useFetchGlobalAndCommunityVectorLayers";
import { TailSpin } from "react-loader-spinner";
import InfiniteScroll from "Components/Geography/InfiniteScroll";
import ReferenceVectorLayersList from "../../ReferenceVectorLayersList";
import usePermissions from "hooks/usePermissions";

const initialValues = {
  description: "",
  vectorLayers: [],
  type: "shape",
  name: "",
  mapData: {},
  shapeType: "",
  color: "#FF0000",
  borderColor: "#FF0000",
};

const validationSchema = Yup.object().shape({
  description: Yup.string()
    .max(255, "255 max characters")
    .required("Description is required"),
  vectorLayers: Yup.array(),
  name: Yup.string().required("Name is required"),
  shapeType: Yup.string().required("Shape Type is required"),
  mapData: Yup.object(),
  color: Yup.string().required("Fill Color is required"),
  borderColor: Yup.string().required("Border Color is required"),
});

const SHAPE_TYPES = [
  { value: "road", label: "Road" },
  { value: "trails", label: "Trails" },
  { value: "river_or_stream", label: "River or Stream" },
  { value: "view", label: "View" },
];

const LineIcon = () => {
  return <div className="bg-secondaryMid w-[1px] h-[24px] rounded-[1px]"></div>;
};

interface DrawShapesInterface {
  height: string;
}

export const DrawShapes = ({ height }: DrawShapesInterface) => {
  const { currentSelectedShape } = useSelector(
    (state: RootState) => state.geography
  );
  const [page, setPage] = useState({
    vector: 1,
    publicVector: 1,
  });
  const [entityType, setEntityType] = useState<EntityType>(null);
  const [currentOpenToggle, setCurrentOpenToggle] = useState<
    "vector" | "publicVector" | null
  >(null);
  const {
    communityLayersLoading,
    communityVectorLayers,
    globalLayersLoading,
    globalVectorLayers,
  } = useFetchGlobalAndCommunityVectorLayers({
    vectorLayerType: "shape",
    entityType,
    currentOpenToggle,
    setCurrentOpenToggle,
    page,
  });
  const { shapeCoordinates } = useSelector(
    (state: RootState) => state.geography
  );
  const { communities } = usePermissions();

  const { communityId } = useParams();
  const dispatch = useDispatch();

  const shapes = [
    {
      name: shapeTypes.LINE,
      Icon: <LineIcon />,
    },
    {
      name: shapeTypes.POLYGON,
      Icon: <PolygonIcon />,
    },
    {
      name: shapeTypes.CIRCLE,
      Icon: <CircleIcon />,
    },
    {
      name: shapeTypes.RECTANGLE,
      Icon: <RectangleIcon />,
    },
  ];

  useEffect(() => {
    // reset selected layers
    dispatch(resetMap(true));
    return () => {
      dispatch(showSnapingControl(false));
    };
  }, []);

  // handlers
  const handleSubmitForm = async (
    values: any,
    { setSubmitting, setFieldError }: any
  ) => {
    if (!willColorBeVisibleOnMap(values.color)) {
      setFieldError(
        "color",
        "Please select a fill color that is easily visible"
      );
      return;
    }
    if (!willColorBeVisibleOnMap(values.borderColor)) {
      setFieldError(
        "borderColor",
        "Please select a border color that is easily visible"
      );
      return;
    }

    const payload = { ...values };

    delete payload.vectorLayers;

    if (!shapeCoordinates) {
      setFieldError("mapData", "Shape is required");
      return;
    }

    payload.mapData = { type: "FeatureCollection", features: shapeCoordinates };

    try {
      if (communityId) {
        await apiLibrary.geography.createLayer(communityId, payload);
        Toasts.success(`Shape layer created successfully`);
        // navigate to all layers screen
        dispatch(openAllLayersListingScreen());
      }
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message ?? error?.message;
      Toasts.error(errorMsg);
    } finally {
      setSubmitting(true);
    }
  };
  // const handleClickOnShape = (name: any) => {
  //   dispatch(removeAllDrawShapes({ remove: true, variant: "local_draw" }));

  //   // SetDoActiveShape(name);

  //   dispatch(updateSelectedShape(name));
  // };

  const handleClickOnShape = (name: any) => {
    dispatch(removeAllDrawShapes({ remove: true, variant: "local_draw" }));
    if (currentSelectedShape === name) {
      dispatch(updateSelectedShape(""));
      setTimeout(() => {
        dispatch(updateSelectedShape(name));
      }, 100);
    } else {
      // SetDoActiveShape(name);
      dispatch(updateSelectedShape(name));
    }
  };
  const handleSelectVectorLayer = (
    e: any,
    item: any,
    values: any,
    setFieldValue: any
  ) => {
    const layerId = e.target.value;
    const isLayerIdExist = values.vectorLayers.find(
      (layer: any) => layer === parseInt(layerId)
    );

    setFieldValue(
      "vectorLayers",
      isLayerIdExist
        ? values.vectorLayers.filter(
            (layer: any) => layer !== parseInt(layerId)
          )
        : [...values.vectorLayers, parseInt(layerId)]
    );
  };
  const handleClickOnTogglerBtn = (type: EntityType) => {
    if (currentOpenToggle !== type) setCurrentOpenToggle(type);
  };
  const handleScrollEnd = (type: EntityType) => {
    const totalPages =
      type === "vector"
        ? communityVectorLayers.totalPages
        : globalVectorLayers.totalPages;
    if (page && type && totalPages >= page[type] + 1) {
      setPage((prev: any) => ({
        ...prev,
        [type]: prev[type] + 1,
      }));

      if (entityType !== type) {
        setEntityType(type);
      }
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmitForm}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        setFieldError,
      }) => (
        <form onSubmit={handleSubmit}>
          <div className="overflow-y-auto" style={{ height }}>
            <h1 className="mb-2 dark:text-textMain">Draw On The Map</h1>
            <div className="flex justify-between w-full mt-1">
              {shapes.map((el, index) => {
                return (
                  <button
                    type="button"
                    key={index}
                    className={`border border-lineMid dark:border-lineLight w-[23%] rounded-lg p-2 flex flex-col justify-center items-center capitalize ${
                      currentSelectedShape === el.name ? "bg-[#005C8924]" : " "
                    }`}
                    onClick={() => handleClickOnShape(el.name)}
                  >
                    <p>{el.Icon}</p>
                    <p
                      className={`mt-2 text-sm font-extralight ${
                        currentSelectedShape === el.name
                          ? "text-primaryDark font-medium dark:text-textMain"
                          : "dark:text-textMain"
                      }`}
                    >
                      {el.name}
                    </p>
                  </button>
                );
              })}
            </div>
            {errors.mapData && (
              <p className="flex-grow pt-1 text-xs text-left text-accent_1Dark">
                Shape is Required
              </p>
            )}
            <p className="text-[15px] text-textMidLight italic my-4 dark:text-textMain ">
              Turn on the vector layers to see them on the map. Click the
              snapping icon to enabel snapping for this layer.
            </p>
            <div>
              <ReferenceVectorLayersList
                selectLayersHandler={(e: any, item: any) =>
                  handleSelectVectorLayer(e, item, values, setFieldValue)
                }
                layerIds={values.vectorLayers}
                layers={globalVectorLayers.vector}
                heading={"Snapping to Global layers"}
                message="Create vector layers first from the Geography"
                loading={globalLayersLoading}
                type="publicVector"
                newItemType="vector"
                layerRenderingMode="draw"
                handleScrollEnd={handleScrollEnd}
                doShowDataList={currentOpenToggle === "publicVector"}
                handleClickOnTogglerBtn={handleClickOnTogglerBtn}
              />
              <ReferenceVectorLayersList
                selectLayersHandler={(e: any, item: any) =>
                  handleSelectVectorLayer(e, item, values, setFieldValue)
                }
                layerIds={values.vectorLayers}
                layers={communityVectorLayers.vector}
                heading={"Snapping to Community layers"}
                message="Create vector layers first from the Listing section"
                loading={communityLayersLoading}
                type="vector"
                newItemType="vector"
                layerRenderingMode="draw"
                handleScrollEnd={handleScrollEnd}
                doShowDataList={currentOpenToggle === "vector"}
                handleClickOnTogglerBtn={handleClickOnTogglerBtn}
              />

              {errors.vectorLayers && (
                <p className="flex-grow text-xs text-left capitalize text-accent_1Dark">
                  {errors.vectorLayers}
                </p>
              )}
            </div>
            <div className="flex flex-col items-start justify-start ">
              <TextInput
                label="Shape Name*"
                type="text"
                placeholder="Shape name"
                name="name"
                handleChange={handleChange}
                handleBlur={handleBlur}
                value={values.name}
                error={errors?.name}
                touched={touched?.name}
              />
            </div>

            <div className="flex flex-col items-start justify-start w-full mb-2">
              <CustomSelect
                data={SHAPE_TYPES}
                placeholder="Select road, trails, river or stream , view"
                label="Type*"
                name={`shapeType`}
                handleOnChange={(selectedOpt: any) =>
                  setFieldValue(`shapeType`, selectedOpt.value)
                }
                touched={touched.shapeType}
                error={errors?.shapeType}
              />
            </div>
            <div className="w-full mb-4">
              <TextInput
                label="Description*"
                type="text"
                rows={3}
                fieldAs="textarea"
                placeholder="Description"
                name="description"
                handleChange={handleChange}
                handleBlur={handleBlur}
                value={values.description}
                error={errors?.description}
                touched={touched?.description}
              />

              {!errors?.description && (
                <p className="flex-grow text-xs text-left text-textMidLight dark:text-textMain">
                  255 max characters
                </p>
              )}
            </div>
            <div className="w-full mb-4 ">
              <div>
                <p
                  className={`flex-grow pb-1 w-full text-sm font-medium text-left capitalize text-secondaryMid dark:text-caption ${
                    touched && errors?.color
                      ? "text-accent_1Dark dark:text-accent_1Dark"
                      : ""
                  }`}
                >
                  Select Fill Color
                </p>
                <ColorPicker
                  handlePicker={(color: string) =>
                    setFieldValue("color", color)
                  }
                  color={values.color}
                />
              </div>
              {errors?.color && (
                <p
                  className={`flex-grow text-xs text-left   ${
                    errors?.color
                      ? "text-accent_1Dark dark:text-accent_1Dark"
                      : "text-textMidLight dark:text-textMain"
                  } `}
                >
                  {errors.color}
                </p>
              )}
            </div>
            {currentSelectedShape &&
              currentSelectedShape !== shapeTypes.LINE && (
                <div className="w-full mb-4 ">
                  <div>
                    <p
                      className={`flex-grow pb-1 w-full text-sm font-medium text-left capitalize text-secondaryMid dark:text-caption ${
                        touched && errors?.borderColor
                          ? "text-accent_1Dark dark:text-accent_1Dark"
                          : ""
                      }`}
                    >
                      Select Border Color
                    </p>
                    <ColorPicker
                      handlePicker={(color: string) =>
                        setFieldValue("borderColor", color)
                      }
                      color={values.borderColor}
                    />
                  </div>
                  {errors?.borderColor && (
                    <p
                      className={`flex-grow text-xs text-left   ${
                        errors?.borderColor
                          ? "text-accent_1Dark dark:text-accent_1Dark"
                          : "text-textMidLight dark:text-textMain"
                      } `}
                    >
                      {errors.borderColor}
                    </p>
                  )}
                </div>
              )}
          </div>
          <div className="flex justify-between gap-2 pb-2">
            <Button
              disabled={isSubmitting}
              type="reset"
              text="Cancel"
              filledColor="primary"
              outlinedColor="primary"
              textColor="textWhite"
              className="px-5 py-2 w-[48.5%]"
              width="[48.t%]"
              height="13"
              fontStyle="font-semibold"
              variant="outlined"
              onClick={() => dispatch(openAllLayersListingScreen())}
            />
            <Button
              type="submit"
              text="Save"
              disabled={
                isSubmitting || !communities.canCreateGeographyCommunities
              }
              filledColor="primary"
              outlinedColor="primary"
              textColor="textWhite"
              className="px-5 py-2 w-[48.5%]"
              width="[48.5%]"
              height="13"
              fontStyle="font-semibold"
              variant="filled"
            />
          </div>
        </form>
      )}
    </Formik>
  );
};

const CustomSelect = ({
  data,
  placeholder,
  label,
  handleOnChange,
  name,
  error,
  touched,
}: any) => {
  const [selectedOpt, setSelectedOpt] = useState<any>(null);
  const [doShowMenu, setDoShowMenu] = useState(false);

  const handleClickOnOption = (opt: any) => {
    setSelectedOpt(opt);
    handleOnChange({ value: opt.value, name });
    toggleMenu();
  };
  const toggleMenu = () => {
    setDoShowMenu(!doShowMenu);
  };
  return (
    <div className="flex flex-col w-full">
      <label className="w-full pb-1 text-sm text-left capitalize text-secondaryMidLight dark:text-caption">
        {label}
      </label>
      <div className="relative">
        <button
          type="button"
          className={`border ${
            error && touched
              ? "border-accent_1Dark"
              : "border-lineDark dark:border-lineLight"
          } px-3 py-2 rounded flex justify-between items-center cursor-pointer w-full`}
          onClick={toggleMenu}
        >
          <span
            className={`${
              selectedOpt
                ? "text-secondary dark:text-textMain"
                : "text-textLightExtra dark:text-caption"
            } text-base`}
          >
            {selectedOpt && selectedOpt.label ? selectedOpt.label : placeholder}
          </span>
          <CheveronDownIcon className="w-6 text-secondary" />
        </button>
        {touched && error && (
          <p className="flex-grow w-[1/2] text-xs text-left text-accent_1Dark pt-2">
            {error}
          </p>
        )}
        {doShowMenu && (
          <div className="absolute z-50 flex flex-col items-start w-full py-2 border rounded font-Overpass border-lineDark dark:border-lineLight bg-bgWhite dark:bg-secondaryLight ">
            {data.map((opt: any, index: number) => {
              return (
                <button
                  key={index.toString()}
                  onClick={() => handleClickOnOption(opt)}
                  className={`w-full px-4 py-1 text-left text-secondaryMid ${
                    selectedOpt && selectedOpt.value === opt.value
                      ? "dark:bg-primary_40 dark:text-textMain"
                      : ""
                  } dark:hover:bg-primary_40 dark:text-caption`}
                  type="button"
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
